/**
 * Harvestable props: trees, bushes and rocks. They are added and removed by
 * `WorldDiff`, and a prop resent with the same id is an hp update — a tree's
 * canopy shrinks as it is chopped, a rock loses chunks, a bush loses berries.
 */
import * as THREE from 'three';
import type { Prop } from '@village/shared';
import { PROP_COLORS } from '../palette';
import type { Terrain } from './terrain';

const MAX_HP: Record<Prop['kind'], number> = { tree: 4, bush: 3, rock: 5 };

function mat(color: string, roughness = 0.9): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color: new THREE.Color(color), roughness, metalness: 0, flatShading: true });
}

function box(w: number, h: number, d: number, color: string): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color));
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

/** Deterministic wobble so a forest doesn't look stamped. */
const wobble = (seed: number) => ((Math.sin(seed * 78.233) * 43758.5453) % 1 + 1) % 1;

interface PropView {
  root: THREE.Group;
  kind: Prop['kind'];
  /** parts whose scale follows hp */
  canopy: THREE.Object3D[];
  hp: number;
}

function buildTree(seed: number): PropView {
  const root = new THREE.Group();
  const s = 0.85 + wobble(seed) * 0.3;
  const trunk = box(0.2, 0.7 * s, 0.2, PROP_COLORS.trunk);
  trunk.position.y = 0.35 * s;
  const c1 = box(0.9 * s, 0.58 * s, 0.9 * s, wobble(seed + 1) > 0.5 ? PROP_COLORS.leaf : PROP_COLORS.leafAlt);
  c1.position.y = 0.92 * s;
  c1.rotation.y = wobble(seed + 2) * 0.6;
  const c2 = box(0.6 * s, 0.44 * s, 0.6 * s, wobble(seed + 1) > 0.5 ? PROP_COLORS.leafAlt : PROP_COLORS.leaf);
  c2.position.set(0.05, 1.3 * s, -0.04);
  c2.rotation.y = -wobble(seed + 3) * 0.5;
  root.add(trunk, c1, c2);
  return { root, kind: 'tree', canopy: [c1, c2], hp: MAX_HP.tree };
}

function buildBush(seed: number): PropView {
  const root = new THREE.Group();
  const s = 0.9 + wobble(seed) * 0.25;
  const blob = new THREE.Mesh(new THREE.IcosahedronGeometry(0.34 * s, 0), mat(PROP_COLORS.bush, 0.95));
  blob.castShadow = true;
  blob.receiveShadow = true;
  blob.position.y = 0.3 * s;
  blob.rotation.set(wobble(seed + 1) * 3, wobble(seed + 2) * 3, 0);
  root.add(blob);
  const berries: THREE.Object3D[] = [];
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + wobble(seed + i) * 2;
    const berry = new THREE.Mesh(new THREE.IcosahedronGeometry(0.06, 0), mat(PROP_COLORS.berry, 0.7));
    berry.position.set(Math.cos(a) * 0.24 * s, 0.34 * s + Math.sin(a * 2) * 0.06, Math.sin(a) * 0.24 * s);
    root.add(berry);
    berries.push(berry);
  }
  return { root, kind: 'bush', canopy: berries, hp: MAX_HP.bush };
}

function buildRock(seed: number): PropView {
  const root = new THREE.Group();
  const s = 0.9 + wobble(seed) * 0.3;
  const chunks: THREE.Object3D[] = [];
  const a = box(0.46 * s, 0.32 * s, 0.42 * s, PROP_COLORS.rock);
  a.position.y = 0.16 * s;
  a.rotation.y = wobble(seed + 1) * 1.2;
  const b = box(0.28 * s, 0.24 * s, 0.26 * s, PROP_COLORS.rockDark);
  b.position.set(0.16 * s, 0.32 * s, -0.1 * s);
  b.rotation.y = wobble(seed + 2) * 1.4;
  const c = box(0.2 * s, 0.16 * s, 0.2 * s, PROP_COLORS.rock);
  c.position.set(-0.2 * s, 0.1 * s, 0.16 * s);
  root.add(a, b, c);
  chunks.push(b, c);
  return { root, kind: 'rock', canopy: chunks, hp: MAX_HP.rock };
}

export class PropLayer {
  readonly group = new THREE.Group();
  private views = new Map<string, PropView>();

  constructor(private terrain: Terrain) {
    this.group.name = 'props';
  }

  sync(props: Prop[]): void {
    const seen = new Set<string>();
    for (const p of props) {
      seen.add(p.id);
      this.add(p);
    }
    for (const id of [...this.views.keys()]) if (!seen.has(id)) this.remove(id);
  }

  /** Add, or update in place when the id is already known (an hp change). */
  add(prop: Prop): void {
    const existing = this.views.get(prop.id);
    if (existing) {
      this.setHp(existing, prop.hp);
      existing.root.position.set(prop.x, this.terrain.surfaceY(prop.x, prop.z), prop.z);
      return;
    }
    const seed = prop.x * 31.7 + prop.z * 17.3;
    const view = prop.kind === 'tree' ? buildTree(seed) : prop.kind === 'bush' ? buildBush(seed) : buildRock(seed);
    view.root.position.set(prop.x, this.terrain.surfaceY(prop.x, prop.z), prop.z);
    view.root.userData = { propId: prop.id, kind: prop.kind, pickable: true };
    this.setHp(view, prop.hp);
    this.views.set(prop.id, view);
    this.group.add(view.root);
  }

  remove(id: string): void {
    const view = this.views.get(id);
    if (!view) return;
    this.group.remove(view.root);
    view.root.traverse((o) => {
      const m = o as THREE.Mesh;
      m.geometry?.dispose();
      const mm = m.material as THREE.Material | undefined;
      mm?.dispose();
    });
    this.views.delete(id);
  }

  /** Re-seat props after the ground under them moved. */
  reseat(): void {
    for (const view of this.views.values()) {
      const { x, z } = view.root.position;
      view.root.position.y = this.terrain.surfaceY(x, z);
    }
  }

  private setHp(view: PropView, hp: number): void {
    view.hp = hp;
    const max = MAX_HP[view.kind];
    const t = Math.max(0, Math.min(1, hp / max));
    if (view.kind === 'bush') {
      // berries disappear one by one
      view.canopy.forEach((berry, i) => (berry.visible = i < Math.ceil(t * view.canopy.length)));
      return;
    }
    const s = 0.45 + t * 0.55;
    for (const part of view.canopy) {
      if (view.kind === 'rock') part.visible = t > (part === view.canopy[0] ? 0.55 : 0.25);
      else part.scale.setScalar(s);
    }
  }
}
